import React, { useState } from 'react';
import { Utensils } from 'lucide-react';
import { cn } from '../lib/utils';

const FALLBACK_STYLES = [
    { backgroundColor: "#D8E2D8", borderColor: "#6B8E6B", color: "#3D523D" }, // Sage
    { backgroundColor: "#E6F0F5", borderColor: "#78B4D1", color: "#2D2A26" }, // Sky Blue
    { backgroundColor: "#F9ECE7", borderColor: "#D96C48", color: "#B54D2B" }, // Terracotta
    { backgroundColor: "#F2F0ED", borderColor: "#787570", color: "#4A4744" }, // Stone
    { backgroundColor: "#EAE7E2", borderColor: "#D1CBC3", color: "#2D2A26" }, // Muted
];

const getFallbackStyleIdx = (name) => {
    if (!name) return 0;
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash) % FALLBACK_STYLES.length;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RecipeImage({ src, alt, className, containerClassName, onError }) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    // Normalize the image URL so it always loads from the configured backend over https.
    // The backend stores AI/uploaded images as ABSOLUTE URLs built from request.base_url, which
    // behind the reverse proxy comes out as http:// (or even http://localhost during dev). Desktop
    // Chrome silently upgrades those http images to https, but the Android WebView (cleartext:false)
    // blocks them — so they showed on the web but not in the app. Rewriting any /api/uploads/ path
    // to BACKEND_URL fixes both existing and future images without a DB migration.
    let imageSrc = src || '';
    const uploadsIdx = imageSrc.indexOf('/api/uploads/');
    if (uploadsIdx !== -1) {
        imageSrc = `${BACKEND_URL}${imageSrc.slice(uploadsIdx)}`;
    } else if (imageSrc.startsWith('/') && !imageSrc.startsWith('http')) {
        imageSrc = `${BACKEND_URL}${imageSrc}`;
    } else if (imageSrc.startsWith('http://')) {
        // Any other cleartext external image would be blocked in the app too; try https instead.
        imageSrc = 'https://' + imageSrc.slice('http://'.length);
    }

    const isFallback = !imageSrc || hasError;
    const styleIdx = getFallbackStyleIdx(alt);
    const fallbackStyle = FALLBACK_STYLES[styleIdx];

    return (
        <div
            className={cn("relative overflow-hidden shrink-0 flex items-center justify-center bg-[#E5E1DA] border border-[#D1CBC3]/50 shadow-sm", containerClassName)}
        >
            {/* Skeleton / Placeholder while loading */}
            {!isLoaded && !isFallback && (
                <div
                    className="absolute inset-0 w-full h-full flex items-center justify-center transition-opacity duration-300"
                    style={{ backgroundColor: fallbackStyle.backgroundColor }}
                >
                    <Utensils className="w-1/2 h-1/2 opacity-40 shrink-0" style={{ color: fallbackStyle.borderColor }} strokeWidth={1.5} />
                </div>
            )}

            {isFallback ? (
                <div
                    className="w-full h-full flex flex-col items-center justify-center border text-center p-2"
                    style={fallbackStyle}
                >
                    <Utensils className="w-1/2 h-1/2 opacity-60 shrink-0" strokeWidth={1.5} />
                </div>
            ) : (
                <img
                    src={imageSrc}
                    alt={alt}
                    className={cn(
                        "w-full h-full object-cover transition-opacity duration-500",
                        isLoaded ? "opacity-100" : "opacity-0",
                        className
                    )}
                    loading="lazy"
                    decoding="async"
                    onLoad={() => setIsLoaded(true)}
                    onError={() => {
                        setHasError(true);
                        setIsLoaded(true);
                        onError?.();
                    }}
                />
            )}
        </div>
    );
}
